import React, { useState, useEffect } from 'react';
import { getStudents } from '../services/api';
import { FaEye } from 'react-icons/fa';
import ReactPaginate from 'react-paginate';
import { useLocation } from 'react-router-dom';

const ManageUsers = () => {
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    isOnboardingComplete: '',
  });
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [expandedRows, setExpandedRows] = useState({});
  const [search, setSearch] = useState('');
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const location = useLocation();


  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const data = await getStudents();
        setAllStudents(Array.isArray(data) ? data : data.documents || []);
       } catch (error) {
         console.error('Failed to fetch students:', error);
       }
     };
     fetchStudents();
    }, []);

  useEffect(() => {
        const applyFilters = () => {
          let filtered = [...allStudents];

          if (filters.isOnboardingComplete)
            filtered = filtered.filter((o) => String(o.isOnboardingComplete) === filters.isOnboardingComplete);

          // Search by phone, name, or school name
          if (search.trim()) {
            const lowerSearch = search.toLowerCase();
            filtered = filtered.filter((o) =>
              o.studentName?.toLowerCase().includes(lowerSearch) ||
              o.studentId?.toLowerCase().includes(lowerSearch) ||
              o.email?.toLowerCase().includes(lowerSearch) ||
              o.phoneNumber?.includes(lowerSearch) ||
              o.address?.pincode?.includes(lowerSearch)
            );
          }

          // Sort
          filtered.sort((a, b) => {
            const dateA = a[sort]?.seconds || 0;
            const dateB = b[sort]?.seconds || 0;
            return order === 'asc' ? dateA - dateB : dateB - dateA;
          });

          const start = currentPage * 20;
          const paginated = filtered.slice(start, start + 20);

          setStudents(paginated);
          setPageCount(Math.ceil(filtered.length /20));
        };

        applyFilters();
     }, [allStudents, filters, sort, order, currentPage, search]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const idFromUrl = params.get('studentId');
        const statusFromUrl = params.get('status'); // New param

        if (idFromUrl) {
          setSearch(idFromUrl);
        }

        if (statusFromUrl) {
          setFilters(prev => ({
            ...prev,
            status: statusFromUrl
          }));
        }
      }, [location.search]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setCurrentPage(0);
  };

  const handleSortChange = (field) => {
    setSort(field);
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setCurrentPage(0);
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

return (
   <div className="p-6">
     <h2 className="text-2xl font-poppins text-driveeasy-blue dark:text-driveeasy-blue2 mb-4">Manage Students</h2>
     {/* Filters */}
     <div className="mb-4 flex flex-wrap items-center gap-4">
         <input
           type="text"
           placeholder="Search by name,id, phone..."
           className="p-2 border rounded-md w-72 bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200"
           value={search}
           onChange={(e) => setSearch(e.target.value)}
         />
       <select
         name="status"
         value={filters.status}
         onChange={handleFilterChange}
         className="p-2 border rounded-md bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200"
       >
         <option value="">All Statuses</option>
         <option value="active">Active</option>
         <option value="inactive">Inactive</option>
       </select>
       <select
         name="isOnboardingComplete"
         value={filters.isOnboardingComplete}
         onChange={handleFilterChange}
         className="p-2 border rounded-md bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200"
       >
         <option value="">All Onboarding</option>
         <option value="true">Onboarding Complete</option>
         <option value="false">Onboarding Incomplete</option>
       </select>
     </div>
     {/* Table */}
     <div className="overflow-x-auto">
       <table className="min-w-full bg-white dark:bg-neutral-800 shadow rounded-lg text-sm border border-gray-100 dark:border-neutral-800">
         <thead>
           <tr className="bg-driveeasy-blue text-white dark:bg-neutral-700 dark:text-gray-200">
             <th className="p-3 text-left">Student ID</th>
             <th className="p-3 text-left">Student Name</th>
             <th className="p-3 text-left">Phone Number</th>
             <th className="p-3 text-left">Status</th>
             <th
               className="p-3 text-left cursor-pointer"
               onClick={() => handleSortChange('createdAt')}
             >
               Joined Date {sort === 'createdAt' && (order === 'asc' ? '↑' : '↓')}
             </th>
             <th className="p-3 text-left">Actions</th>
           </tr>
         </thead>
         <tbody>
           {students.length > 0 ? students.map((student) => (
             <React.Fragment key={student.studentId}>
               <tr className="border-b border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-700/70 text-black dark:text-gray-300">
                 <td
                     className="p-3 text-driveeasy-blue hover:underline cursor-pointer"
                     onClick={() => {
                       setSelectedStudent(student);
                       setProfileModalOpen(true);
                     }}
                 >{student.studentId}
                 </td>
                 <td className="p-3">{student.studentName || 'N/A'}</td>
                 <td className="p-3">{student.phoneNumber|| 'N/A'}</td>
                 <td className="p-3">
                   <span
                     className={`px-2 py-1 rounded ${
                       student.bookings?.length > 0 ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'
                     }`}
                   >
                     {student.bookings?.length > 0 ? 'Active' : 'Inactive'}
                   </span>
                 </td>
                 <td className="p-3">
                 {student.createdAt?.seconds ? new Date(student.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                    </td>
                 <td className="p-3">
                   <button onClick={() => toggleRow(student.studentId)}>
                    <FaEye className={expandedRows[student.studentId] ? 'text-driveeasy-yellow' : 'text-driveeasy-blue'} />
                   </button>
                 </td>
               </tr>
               {expandedRows[student.studentId] && (
                 <tr>
                  <td colSpan="8" className="p-3 bg-gray-50 text-sm dark:bg-neutral-800">
                     <div className="grid grid-cols-2 gap-4">
                     <div>
                      <h4 className="font-poppins text-driveeasy-blue">Address Details</h4>
                      <p>Address: {student.address?.addressLine1 || 'N/A'}</p>
                      <p>City: {student.address?.city || 'N/A'}</p>
                      {student.address?.floor && <p>Floor: {student.address?.floor || 'N/A'}</p>}
                      <p>Full Address: {student.address?.fullAddress || 'N/A'}</p>
                      {student.address?.landmark && <p>Landmark: {student.address?.landmark || 'N/A'}</p>}
                      <p>Location: {student.address?.lat || 'N/A'}, {student.address?.lng || 'N/A'}</p>
                      <p>Locality: {student.address?.locality || 'N/A'}</p>
                      <p>Pincode: {student.address?.pincode || 'N/A'}</p>
                      <p>State: {student.address?.state || 'N/A'}</p>
                    </div>
                       <div>
                         <h4 className="font-poppins text-driveeasy-blue">Student Details</h4>
                         <p>Email: {student.email || 'N/A'}</p>
                         <p>Date of Birth: {student.dateOfBirth || 'N/A'}</p>
                         <p>Age: {student.age || 'N/A'}</p>
                         <p>Onboarding Complete: {student.isOnboardingComplete ? '✅' : '❌'}</p>
                       </div>
                     </div>
                   </td>
                 </tr>
               )}
             </React.Fragment>
           )): (
           <tr>
             <td colSpan="8" className="text-center p-4">No records found.</td>
           </tr>
         )}
         </tbody>
       </table>
     </div>
     {/* Pagination */}
     <ReactPaginate
       previousLabel={'Previous'}
       nextLabel={'Next'}
       pageCount={pageCount}
       onPageChange={handlePageChange}
       containerClassName={'flex justify-center mt-4 space-x-2'}
       pageClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-600 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
      activeClassName={'bg-driveeasy-blue text-white'}
      previousClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-700 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
      nextClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-700 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
    />
     {profileModalOpen && selectedStudent && (
       <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
         <div className="bg-white dark:bg-neutral-800 dark:text-gray-200 p-6 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-xl border border-gray-200 dark:border-neutral-700">
           <h3 className="text-xl font-bold mb-4 text-driveeasy-blue">Full Profile</h3>
           <div className="grid grid-cols-2 gap-4 text-sm">
             <div>
               <h4 className="font-semibold text-driveeasy-blue">Student Info</h4>
               <p><strong>UID:</strong> {selectedStudent.studentId || 'N/A'}</p>
               <p><strong>Student Name:</strong> {selectedStudent.studentName || 'N/A'}</p>
               <p><strong>Phone:</strong> {selectedStudent.phoneNumber || 'N/A'}</p>
               <p><strong>Email:</strong> {selectedStudent.email || 'N/A'}</p>
               <p><strong>Onboarding Complete:</strong> {selectedStudent.isOnboardingComplete ? 'Yes' : 'No'}</p>
             </div>
             <div>
               <h4 className="font-semibold text-driveeasy-blue">Address</h4>
               <p><strong>Address:</strong> {selectedStudent.address?.addressLine1 || 'N/A'}</p>
               <p><strong>City:</strong> {selectedStudent.address?.city || 'N/A'}</p>
               <p><strong>State:</strong> {selectedStudent.address?.state || 'N/A'}</p>
               <p><strong>Pincode:</strong> {selectedStudent.address?.pincode || 'N/A'}</p>
               <p><strong>Landmark:</strong> {selectedStudent.address?.landmark || 'N/A'}</p>
             </div>
           </div>

           <div className="mt-4 flex justify-end">
             <button
               className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
               onClick={() => setProfileModalOpen(false)}
             >
               Close
             </button>
           </div>
         </div>
       </div>
     )}

   </div>
 );
};

export default ManageUsers;